import { useState, useEffect, useCallback } from 'react';
import { notificationsService } from '@/services/notificationsService';
import {
  cmsService,
  type Case,
  type Alert,
  type IOC,
  type CaseSummary,
  type AlertSummary,
  type EscalationKpis,
  type AnalystAvailability,
  type GetCasesQuery,
  type GetAlertsQuery,
  type GetIocsQuery,
  type Pagination,
  type AlertSource,
  type CmsOrgUser,
  type CmsRole,
  type CmsRoleWithPermissions,
  type CurrentPlanResponse,
  type PlanDefinition,
  type SubscriptionRequest,
  type SubscribedOrganization,
  type GetOperationalTasksQuery,
  type GetMyTasksQuery,
  type OperationalTask,
  type TaskDashboardSummary,
  type GetCaseBoardQuery,
  type CaseBoardData,
  type CaseBoardDashboard,
} from '../services/cmsService';

// ── useCaseSummary ──────────────────────────────────────────────────────────

export function useCaseSummary() {
  const [summary, setSummary] = useState<CaseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.getCaseSummary();
      setSummary(data);
    } catch (err: any) {
      // Silently handle 403 — non-manager roles don't have access to summary endpoint
      if (err?.response?.status === 403) {
        setSummary(null);
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to load case summary');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { summary, loading, error, refresh: fetch };
}

// ── useAlertSummary ─────────────────────────────────────────────────────────

export function useAlertSummary() {
  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.getAlertSummary();
      setSummary(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load alert summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { summary, loading, error, refresh: fetch };
}

// ── useCases ────────────────────────────────────────────────────────────────

export function useCases(initialQuery?: GetCasesQuery) {
  const [cases, setCases] = useState<Case[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (query?: GetCasesQuery) => {
    setLoading(true);
    setError(null);
    try {
      const res = await cmsService.listCases(query);
      setCases(res.data);
      setPagination(res.pagination);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(initialQuery); }, [fetch]);

  return { cases, pagination, loading, error, fetch, clearError: () => setError(null) };
}

// ── useCase ─────────────────────────────────────────────────────────────────

export function useCase(id: string | undefined) {
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (opts?: { clear?: boolean }) => {
    if (!id) {
      setCaseData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    if (opts?.clear !== false) {
      setCaseData(null);
    }
    try {
      const data = await cmsService.getCase(id);
      setCaseData(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load case');
      setCaseData(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch({ clear: true });
  }, [fetch]);

  const refresh = useCallback(() => fetch({ clear: false }), [fetch]);

  return { caseData, loading, error, refresh };
}

// ── useAlerts ───────────────────────────────────────────────────────────────

export function useAlerts(initialQuery?: GetAlertsQuery) {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (query?: GetAlertsQuery) => {
    setLoading(true);
    setError(null);
    try {
      const res = await cmsService.listAlerts(query);
      setAlerts(res.data);
      setPagination(res.pagination);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(initialQuery); }, [fetch]);

  return { alerts, pagination, loading, error, fetch, clearError: () => setError(null) };
}

// ── useAlert ────────────────────────────────────────────────────────────────

export function useAlert(id: string | undefined) {
  const [alert, setAlert] = useState<Alert | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!id) {
      setAlert(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.getAlert(id);
      setAlert(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load alert details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { alert, loading, error, refresh: fetch, clearError: () => setError(null) };
}

// ── useIOCs ─────────────────────────────────────────────────────────────────

export function useIOCs(initialQuery?: GetIocsQuery) {
  return {
    iocs: [],
    pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
    loading: false,
    error: null,
    fetch: async () => {},
    clearError: () => {},
  };
}

// ── useAlertSources ─────────────────────────────────────────────────────────

export function useAlertSources() {
  const [sources, setSources] = useState<AlertSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.listAlertSources();
      setSources(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load alert sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { sources, loading, error, refresh: fetch, clearError: () => setError(null) };
}

// ── useOrgUsers ─────────────────────────────────────────────────────────────

export function useOrgUsers() {
  const [users, setUsers] = useState<CmsOrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.listOrgUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { users, loading, error, refresh: fetch, clearError: () => setError(null) };
}

// ── useRoles ────────────────────────────────────────────────────────────────

export function useRoles() {
  const [roles, setRoles] = useState<CmsRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.listRoles();
      setRoles(data.roles ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { roles, loading, error, refresh: fetch };
}

// ── usePermissionsMatrix ────────────────────────────────────────────────────

export function usePermissionsMatrix() {
  const [matrix, setMatrix] = useState<Record<string, CmsRoleWithPermissions>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.getPermissionsMatrix();
      setMatrix(data.matrix ?? {});
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { matrix, loading, error, refresh: fetch };
}

// Notifications: moved off cmsService entirely — the CMS notifications page
// and the sidebar badge now read from notification-service directly via
// @/services/notificationsService and @/hooks/useNotificationSocket, the
// same backend the shared Header bell already used. See
// modules/cms/pages/CmsNotificationsPage.tsx and layouts/CmsSidebar.tsx.

// Audit: moved off this hook entirely — CmsAuditPage now reads from
// audit-service directly via @/services/auditService, the same backend the
// main app's AuditLogPage already used.

// ── useCurrentPlan ──────────────────────────────────────────────────────────

export function useCurrentPlan() {
  const [plan, setPlan] = useState<CurrentPlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.getCurrentPlan();
      setPlan(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load plan info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { plan, loading, error, refresh: fetch };
}

// ── useActivateTrial ─────────────────────────────────────────────────────────

export function useActivateTrial(onSuccess?: () => void) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await cmsService.activateTrial();
      onSuccess?.();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to activate trial');
    } finally {
      setLoading(false);
    }
  }, [onSuccess]);

  return { activate, loading, error };
}

// ── usePlans ────────────────────────────────────────────────────────────────

export function usePlans() {
  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.listPlans();
      setPlans(data.plans ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { plans, loading, error, refresh: fetch };
}

// ── useSubscriptionRequests ─────────────────────────────────────────────────

export function useSubscriptionRequests() {
  const [requests, setRequests] = useState<SubscriptionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.listSubscriptionRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load subscription requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { requests, loading, error, refresh: fetch };
}

// ── useSubscribedOrganizations ──────────────────────────────────────────────

export function useSubscribedOrganizations() {
  const [organizations, setOrganizations] = useState<SubscribedOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.listSubscribedOrganizations();
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load subscribed organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { organizations, loading, error, refresh: fetch };
}

// ── useEscalationKpis ──────────────────────────────────────────────────────

export function useEscalationKpis() {
  const [kpis, setKpis] = useState<EscalationKpis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.getEscalationKpis();
      setKpis(data);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setKpis(null);
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to load escalation KPIs');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { kpis, loading, error, refresh: fetch };
}

// ── useAssignedCases ────────────────────────────────────────────────────────

export function useAssignedCases() {
  const [cases, setCases] = useState<Case[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cmsService.getAssignedCases();
      setCases(res.data ?? []);
      setTotal(res.total ?? 0);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setCases([]);
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to load assigned cases');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { cases, total, loading, error, refresh: fetch };
}

// ── useActiveInvestigations ─────────────────────────────────────────────────

export function useActiveInvestigations() {
  const [cases, setCases] = useState<Case[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cmsService.getActiveInvestigations();
      setCases(res.data ?? []);
      setTotal(res.total ?? 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load active investigations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { cases, total, loading, error, refresh: fetch };
}

// ── useAnalystAvailability ──────────────────────────────────────────────────

export function useAnalystAvailability(userId: string | undefined) {
  const [availability, setAvailability] = useState<AnalystAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cmsService.getAnalystAvailability(userId);
      setAvailability(data);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setAvailability(null);
      } else {
        setError(err?.response?.data?.message || err?.message || 'Failed to load availability');
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { availability, loading, error, refresh: fetch };
}

// ── useOperationalTasks ─────────────────────────────────────────────────────

export function useOperationalTasks(query?: GetOperationalTasksQuery) {
  return { tasks: [], pagination: null, loading: false, error: null, refresh: async () => {} };
}

// ── useMyTasks ──────────────────────────────────────────────────────────────

export function useMyTasks(query?: GetMyTasksQuery) {
  return { tasks: [], pagination: null, loading: false, error: null, refresh: async () => {} };
}

// ── useTaskDashboard ────────────────────────────────────────────────────────

export function useTaskDashboard() {
  return { summary: null, loading: false, error: null, refresh: async () => {} };
}

// ── useCaseBoard ────────────────────────────────────────────────────────────

export function useCaseBoard(filters?: GetCaseBoardQuery) {
  const [data, setData] = useState<CaseBoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await cmsService.getCaseBoard(filters);
      setData(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load case board');
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refresh: fetch };
}

// ── useCaseBoardDashboard ───────────────────────────────────────────────────

export function useCaseBoardDashboard() {
  const [dashboard, setDashboard] = useState<CaseBoardDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await cmsService.getCaseBoardDashboard();
      setDashboard(result);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { dashboard, loading, error, refresh: fetch };
}

// ── useUnreadCount ──────────────────────────────────────────────────────────

export function useUnreadCount(pollIntervalMs = 30_000) {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const res = await notificationsService.count();
      setCount(res.unreadCount ?? 0);
    } catch {
      // silently ignore — badge is non-critical
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, pollIntervalMs);
    return () => clearInterval(id);
  }, [refresh, pollIntervalMs]);

  return { count, refresh };
}
